import { useState } from 'react';
import { Loader2, Search, Wrench, Unlink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

// Generic repairable/orphaned/healthy census + repair/release card, shared by
// every "confirmed at the match-doc level but the live transaction disagrees"
// diagnostics panel (recurring-cost, statement-payout, ...). The bug class is
// identical across matchers (CLAUDE.md section 7 item 4: hand-maintained
// duplicate logic drifting apart) -- only the API calls and the columns shown
// per row differ, so those are the only things callers configure.
//
// releaseBuckets is a list because a caller can have more than one dead-side
// classification needing its own Release action (statement-payout has
// orphaned_transaction AND orphaned_statement -- see
// services/statement_payout_matcher.py::classify_confirmed_matches); a
// caller with only one dead-side case (recurring-cost) just passes a
// single-entry list. Every bucket's Release button calls the same
// repairRelease(matchId) -- the backend disambiguates by looking the match
// up itself, never by which button was clicked.
//
// Neither the repairable nor any release bucket self-heals on its own:
// repairable is fixed in place via Repair Apply (same idempotent confirm
// helper every matcher's fresh-apply path already uses); a release bucket
// has a dead side with nothing left to stamp/re-point in place, so each row
// gets its own Release action. Preview first, then Apply/Release as separate
// explicit actions -- never auto-fixed during the preview itself (CLAUDE.md
// section 6).
export function MatchRepairPanel({
  title,
  description,
  repairPreview,
  repairApply,
  repairRelease,
  repairableColumns,
  releaseBuckets,
  repairableCaption = 'Repairable (transaction exists, unstamped)',
  repairButtonLabel = 'Repair',
}) {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [releasingId, setReleasingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const runPreview = async () => {
    setPreviewing(true);
    setApplyResult(null);
    try {
      const { data } = await repairPreview();
      setPreview(data);
      const repairableCount = data.repairable?.count ?? 0;
      const releaseCount = releaseBuckets.reduce((sum, b) => sum + (data[b.key]?.count ?? 0), 0);
      if (repairableCount > 0 || releaseCount > 0) {
        toast.warning(`${repairableCount} repairable, ${releaseCount} orphaned confirmation(s) found`);
      } else {
        toast.success('No stuck confirmations found');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await repairApply();
      setApplyResult(data);
      toast.success(`${data.repaired?.length ?? 0} repaired, ${data.failed?.length ?? 0} failed`);
      const { data: refreshed } = await repairPreview();
      setPreview(refreshed);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const runRelease = async (matchId) => {
    setReleasingId(matchId);
    try {
      await repairRelease(matchId);
      toast.success(`${matchId} released -- reopened for matching`);
      const { data: refreshed } = await repairPreview();
      setPreview(refreshed);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setReleasingId(null);
    }
  };

  const repairable = preview?.repairable ?? { count: 0, sample: [] };
  const buckets = releaseBuckets.map((b) => ({ ...b, data: preview?.[b.key] ?? { count: 0, sample: [] } }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Wrench className="h-4 w-4 text-amber-500" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={runPreview} disabled={previewing}>
            {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
            Preview
          </Button>
          <Button
            size="sm"
            onClick={runApply}
            disabled={applying || !preview || repairable.count === 0}
          >
            {applying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wrench className="h-4 w-4 mr-1" />}
            {repairButtonLabel} {repairable.count ? `${repairable.count} match(es)` : ''}
          </Button>
        </div>

        {preview && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {repairable.count} repairable
              {buckets.map((b) => `, ${b.data.count} ${b.summaryLabel ?? b.key}`).join('')}
              , {preview.healthy_count ?? 0} healthy
            </p>

            {repairable.sample?.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium">{repairableCaption}</p>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        {repairableColumns.map((col) => (
                          <th key={col.key} className="text-left px-3 py-2 font-medium">{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {repairable.sample.map((m) => (
                        <tr key={m.id} className="border-b last:border-0">
                          {repairableColumns.map((col) => (
                            <td key={col.key} className={col.className ?? 'px-3 py-2 font-mono text-xs'}>
                              {col.key === 'confirm_step' ? (m[col.key] ?? 0) : (m[col.key] ?? '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {buckets.map((bucket) => bucket.data.sample?.length > 0 && (
              <div key={bucket.key} className="space-y-1">
                <p className="text-xs font-medium text-red-600 dark:text-red-400">{bucket.caption}</p>
                <div className="overflow-x-auto border rounded-lg border-red-200 dark:border-red-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        {bucket.columns.map((col) => (
                          <th key={col.key} className="text-left px-3 py-2 font-medium">{col.label}</th>
                        ))}
                        <th className="text-left px-3 py-2 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bucket.data.sample.map((m) => (
                        <tr key={m.id} className="border-b last:border-0">
                          {bucket.columns.map((col) => (
                            <td key={col.key} className={col.className ?? 'px-3 py-2 font-mono text-xs'}>
                              {col.render ? col.render(m) : (m[col.key] ?? '')}
                            </td>
                          ))}
                          <td className="px-3 py-2 text-xs">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => runRelease(m.id)}
                              disabled={releasingId === m.id}
                            >
                              {releasingId === m.id ? (
                                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              ) : (
                                <Unlink className="h-3 w-3 mr-1" />
                              )}
                              Release
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        {applyResult && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Last repair: {applyResult.repaired?.length ?? 0} repaired
              {applyResult.failed?.length > 0 && `, ${applyResult.failed.length} failed`}
            </p>
            {applyResult.failed?.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-red-600 dark:text-red-400">
                  Failed on repair too -- this is a data-shaped exception, not a batch-abort.
                  Copy the reason below (full traceback is in the backend log).
                </p>
                <div className="overflow-x-auto border rounded-lg border-red-200 dark:border-red-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Match ID</th>
                        <th className="text-left px-3 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyResult.failed.map((f) => (
                        <tr key={f.match_id} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{f.match_id}</td>
                          <td className="px-3 py-2 text-xs">{f.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
